import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Box, Button, Card, CardContent, Checkbox, Chip, Dialog, FormControlLabel, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, MenuItem, Select, InputLabel, FormControl, Grid, InputAdornment } from '@mui/material';
import { Edit, LocalShipping, Timeline, AddCircle, Delete, Search, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Product, Material, Warehouse } from '../types';
import api from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import TextField from '../components/common/TextField';

// Motivos tabulados de ajuste manual (pedido de la reunión 2026-08-20: que sea
// dropdown y no texto libre, con "Otro" como escape). El catálogo definitivo lo
// pasa el cliente; agregar acá los que falten.
const ADJUST_REASONS = ['Carga inicial', 'Ajuste por corrección', 'Mercadería deteriorada', 'Merma de producción', 'Otro'];

// Roles que pueden ajustar stock y editar lotes (espejo del control server-side).
const STOCK_ROLES = ['admin', 'manager', 'propietario', 'gerente', 'logistica', 'calidad'];
// Roles que pueden liberar un lote a la venta (espejo de StockPermissions.CanReleaseBatch):
// control de calidad / dirección técnica, sin Logística.
const RELEASE_ROLES = ['admin', 'manager', 'propietario', 'gerente', 'calidad', 'direcciontecnica'];

export const InventoryPage: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const canAdjustStock = STOCK_ROLES.includes((user?.role || '').toLowerCase());
  const canReleaseBatch = RELEASE_ROLES.includes((user?.role || '').toLowerCase());
  const [releasingBatchId, setReleasingBatchId] = useState<number | null>(null);
  // Alta manual de muestra de retención (caso borde: la orden se completó sin
  // cargarla y el lote quedó sin poder liberarse).
  const [retentionDialog, setRetentionDialog] = useState<{ batchId: number; quantityRetention: string; quantityAnalysis: string; notes: string } | null>(null);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [perProductLocStocks, setPerProductLocStocks] = useState<Record<number, { location: string; stock: number }[]>>({});
  const [viewMode, setViewMode] = useState<'product'|'warehouse'>('product');
  const [stockTab, setStockTab] = useState<'products'|'materials'>('products');
  const [valuationMode, setValuationMode] = useState<'cost' | 'price'>('cost');
  // No desplegables: mostramos todo abierto
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Product | null>(null);
  const [newStock, setNewStock] = useState<number>(0);
  const [reason, setReason] = useState<string>('Ajuste por corrección');
  const [customReason, setCustomReason] = useState<string>('');
  const [adjustLot, setAdjustLot] = useState<string>('');
  const [adjustUseLots, setAdjustUseLots] = useState(false);
  const [adjustLotsList, setAdjustLotsList] = useState<any[]>([]);
  const [adjustLotQty, setAdjustLotQty] = useState<Record<number, string>>({});
  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const [expandedLots, setExpandedLots] = useState<Record<number, any[]>>({});
  const [adjustExpiration, setAdjustExpiration] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  // Ajuste manual de materia prima (1.1.d)
  const [matAdjust, setMatAdjust] = useState<{ material: Material; type: 'IN' | 'OUT'; quantity: number; reason: string; customReason: string; notes: string; lotNumber: string; expirationDate: string; supplierId: number | '' } | null>(null);
  const [adjustSuppliers, setAdjustSuppliers] = useState<any[]>([]);
  const [matAdjustSaving, setMatAdjustSaving] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  // Edición de lote/vencimiento de un lote de producto terminado (1.1.b)
  const [prodBatchEdit, setProdBatchEdit] = useState<{ id: number; batchCode: string; expirationDate: string } | null>(null);
  const [prodBatchSaving, setProdBatchSaving] = useState(false);
  const saveProdBatchEdit = async () => {
    if (!prodBatchEdit) return;
    setProdBatchSaving(true);
    try {
      await api.updateProductBatch(prodBatchEdit.id, {
        batchCode: prodBatchEdit.batchCode,
        expirationDate: prodBatchEdit.expirationDate || null,
      });
      setProdBatchEdit(null);
      if (traceData?.productId) {
        const data = await api.getProductTraceability(traceData.productId);
        setTraceData(data);
      }
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message || 'No se pudo editar el lote');
    } finally {
      setProdBatchSaving(false);
    }
  };
  // Liberación a venta de un lote de producto terminado (control de calidad).
  const releaseProdBatch = async (batchId: number) => {
    if (!window.confirm('¿Liberar este lote a la venta? Va a quedar disponible para vender.')) return;
    setReleasingBatchId(batchId);
    try {
      await api.releaseProductBatch(batchId);
      if (traceData?.productId) {
        const data = await api.getProductTraceability(traceData.productId);
        setTraceData(data);
      }
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message || 'No se pudo liberar el lote');
    } finally {
      setReleasingBatchId(null);
    }
  };
  const saveRetentionSample = async () => {
    if (!retentionDialog) return;
    const qr = Number(retentionDialog.quantityRetention || 0);
    const qa = Number(retentionDialog.quantityAnalysis || 0);
    if (qr <= 0 && qa <= 0) {
      setAdjustError('Indicá al menos una cantidad (retención o análisis) mayor a cero');
      return;
    }
    setRetentionSaving(true);
    try {
      await api.createRetentionSample({
        inventoryBatchId: retentionDialog.batchId,
        quantityRetention: qr,
        quantityAnalysis: qa,
        notes: retentionDialog.notes || undefined,
      });
      setRetentionDialog(null);
      if (traceData?.productId) {
        const data = await api.getProductTraceability(traceData.productId);
        setTraceData(data);
      }
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message || 'No se pudo registrar la muestra de retención');
    } finally {
      setRetentionSaving(false);
    }
  };
  const [movements, setMovements] = useState<any[]>([]);
  const [movPage, setMovPage] = useState(1);
  const [movTotalPages, setMovTotalPages] = useState(1);
  const [shipOpen, setShipOpen] = useState(false);
  const [shipLines, setShipLines] = useState<{ productId: number; quantity: number }[]>([]);
  const [shipRef, setShipRef] = useState<string>('');
  const [shipNotes, setShipNotes] = useState<string>('');
  const [shipments, setShipments] = useState<any[]>([]);
  const [shipPage, setShipPage] = useState(1);
  const [shipTotalPages, setShipTotalPages] = useState(1);
  const [shipHasMore, setShipHasMore] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [locStocks, setLocStocks] = useState<{ location: string; stock: number }[]>([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [fromLoc, setFromLoc] = useState('Principal');
  const [toLoc, setToLoc] = useState('Principal');
  const [transferQty, setTransferQty] = useState(0);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceData, setTraceData] = useState<any | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Set<string>>(new Set());
  const [flagsReady, setFlagsReady] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [whDialogOpen, setWhDialogOpen] = useState(false);
  const [whForm, setWhForm] = useState<{ id?: number; name: string; type: string; code?: string; address?: string; notes?: string }>({ name: '', type: 'INTERNAL' });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  // MP/ME: el buscador tambien filtra por codigo (y nombre). Antes solo filtraba
  // productos y a las materias primas les cargaba la lista completa, asi que
  // buscar un codigo no hacia nada. Sin mayusculas/tildes; cada palabra tiene que
  // aparecer en el codigo o en el nombre ("pote 600" encuentra ENV-POTE-600).
  const filteredMaterials = useMemo(() => {
    const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const terms = norm(search).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return materials;
    return materials.filter(m => {
      const hay = norm(`${m.code || ''} ${m.name || ''}`);
      return terms.every(t => hay.includes(t));
    });
  }, [materials, search]);

  const load = async () => {
    const res = await api.getProducts({ page: 1, pageSize: 500, search: debouncedSearch || undefined });
    setProducts(res.products);
    // Prefetch per-product location stocks (for quick chips rendering)
    const entries: Record<number, { location: string; stock: number }[]> = {};
    await Promise.all((res.products || []).map(async (p) => {
      try {
        const s = await api.getLocationStocks(p.id);
        entries[p.id] = (s.items || []).filter(i => i.stock > 0).sort((a, b) => b.stock - a.stock);
      } catch { entries[p.id] = []; }
    }));
    setPerProductLocStocks(entries);

    // Cargar materias primas / insumos
    try {
      const mats = await api.getMaterials({ page: 1, pageSize: 500 });
      setMaterials(mats.materials || []);
    } catch {
      setMaterials([]);
    }

    try {
      const ws = await api.getWarehouses();
      setWarehouses(ws.items || []);
    } catch {
      setWarehouses([]);
    }
  };

  const loadShipments = async (page = 1) => {
    try {
      const s = await api.getMlFullShipments({ page, pageSize: 10 });
      setShipments(s.items || []);
      setShipPage(page);
      setShipTotalPages(s.totalPages || 1);
      setShipHasMore(!!s.mlHasMore);
    } catch {
      setShipments([]);
      setShipPage(1);
      setShipTotalPages(1);
      setShipHasMore(false);
    }
  };

  useEffect(() => { load(); }, [debouncedSearch]);
  // Se oculta histórico en esta vista; dejamos de cargarlo aquí
  useEffect(() => { (async () => { const l = await api.getLocations(); setLocations(l.locations || ['Principal']); })(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        setFeatureFlags(new Set((flags || []).filter(f => f.enabled).map(f => f.key.toLowerCase())));
      } catch {
        setFeatureFlags(new Set());
      }
      setFlagsReady(true);
    })();
  }, []);
  useEffect(() => {
    if (flagsReady && hasFlag('inventory-ml-full-flex')) {
      loadShipments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsReady]);

  const hasFlag = (key: string) => featureFlags.has(key.toLowerCase());

  const locationInventory = useMemo(() => {
    const map: Record<string, { product: Product; stock: number }[]> = {};
    for (const p of products) {
      const perLoc = perProductLocStocks[p.id] || [];
      for (const loc of perLoc) {
        if (!map[loc.location]) map[loc.location] = [];
        map[loc.location].push({ product: p, stock: loc.stock });
      }
    }
    // sort each location by stock desc
    Object.keys(map).forEach(loc => map[loc].sort((a, b) => b.stock - a.stock));
    warehouses.forEach(w => {
      if (!map[w.name]) map[w.name] = [];
    });
    return map;
  }, [products, perProductLocStocks, warehouses]);

  const getLocationTag = (loc: string) => {
    const norm = (loc || '').toLowerCase();
    if (norm.includes('depósito ml') || norm.includes('deposito ml') || norm.includes('full') || norm.includes('fulfillment') || norm.includes('meli_facility')) {
      return { label: 'ML FULL', color: 'warning' as const };
    }
    if (norm.includes('secundario')) return { label: 'Secundario', color: 'info' as const };
    if (norm.includes('principal') || norm.includes('propio') || norm.includes('internal')) {
      return { label: 'Interno', color: 'success' as const };
    }
    return { label: 'Depósito', color: 'default' as const };
  };

  const openWarehouseDialog = (wh?: Warehouse) => {
    if (wh) {
      setWhForm({ id: wh.id, name: wh.name, type: wh.type, code: wh.code || '', address: wh.address || '', notes: wh.notes || '' });
    } else {
      setWhForm({ id: undefined, name: '', type: 'INTERNAL', code: '', address: '', notes: '' });
    }
    setWhDialogOpen(true);
  };

  const saveWarehouse = async () => {
    if (!whForm.name.trim()) return;
    if (whForm.id) {
      const updated = await api.updateWarehouse(whForm.id, { name: whForm.name, code: whForm.code, type: whForm.type, address: whForm.address, notes: whForm.notes, isActive: true });
      setWarehouses(prev => prev.map(w => w.id === updated.id ? updated : w));
    } else {
      const created = await api.createWarehouse({ name: whForm.name, code: whForm.code, type: whForm.type, address: whForm.address, notes: whForm.notes });
      setWarehouses(prev => [...prev, created]);
    }
    setWhDialogOpen(false);
  };

  const deleteWarehouse = async (id: number) => {
    await api.deleteWarehouse(id);
    setWarehouses(prev => prev.filter(w => w.id !== id));
  };

  const openAdjust = (p: Product) => {
    setSelected(p);
    setNewStock(p.currentStock);
    setReason('Ajuste por corrección');
    setCustomReason('');
    setAdjustLot('');
    setAdjustExpiration('');
    setNotes('');
    setAdjustError(null);
    setAdjustUseLots(false);
    setAdjustLotQty({});
    setAdjustLotsList([]);
    api.getProductTraceability(p.id)
      .then((r: any) => setAdjustLotsList((r.batches || []).filter((b: any) => b.quantity > 0)))
      .catch(() => setAdjustLotsList([]));
    setOpen(true);
  };

  const toggleExpandProduct = async (productId: number) => {
    if (expandedProductId === productId) { setExpandedProductId(null); return; }
    setExpandedProductId(productId);
    if (!expandedLots[productId]) {
      try {
        const r: any = await api.getProductTraceability(productId);
        setExpandedLots(prev => ({ ...prev, [productId]: (r.batches || []).filter((b: any) => b.quantity > 0) }));
      } catch {
        setExpandedLots(prev => ({ ...prev, [productId]: [] }));
      }
    }
  };

  const saveAdjust = async () => {
    if (!selected) return;
    const finalReason = reason === 'Otro' ? customReason.trim() : reason;
    if (!finalReason) { setAdjustError('Indicá el motivo del ajuste'); return; }
    const bajaAmount = (selected.currentStock ?? 0) - newStock;
    let batchAllocations: { batchId: number; quantity: number }[] | undefined;
    if (newStock < (selected.currentStock ?? 0) && adjustUseLots) {
      batchAllocations = adjustLotsList
        .filter((b: any) => b.isReleased)
        .map(b => ({ batchId: b.id as number, quantity: Number(adjustLotQty[b.id] || 0) }))
        .filter(a => a.quantity > 0);
      const sumLots = batchAllocations.reduce((acc, a) => acc + a.quantity, 0);
      if (Math.abs(sumLots - bajaAmount) > 1e-9) {
        setAdjustError(`La suma de los lotes (${sumLots}) tiene que ser igual a la baja (${bajaAmount})`);
        return;
      }
    }
    try {
      await api.adjustStock(selected.id, newStock, finalReason, notes, adjustLot || undefined, adjustExpiration || undefined, batchAllocations);
      setOpen(false);
      await load();
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message || 'No se pudo guardar el ajuste');
    }
  };

  const openMatAdjust = (m: Material) => {
    setAdjustError(null);
    setMatAdjust({ material: m, type: 'IN', quantity: 0, reason: 'Ajuste por corrección', customReason: '', notes: '', lotNumber: '', expirationDate: '', supplierId: '' });
    if (adjustSuppliers.length === 0) {
      api.getSuppliers({ page: 1, pageSize: 1000 })
        .then(res => setAdjustSuppliers(res.suppliers || []))
        .catch(() => setAdjustSuppliers([]));
    }
  };

  const saveMatAdjust = async () => {
    if (!matAdjust) return;
    const finalReason = matAdjust.reason === 'Otro' ? matAdjust.customReason.trim() : matAdjust.reason;
    if (!finalReason) { setAdjustError('Indicá el motivo del ajuste'); return; }
    if (!(matAdjust.quantity > 0)) { setAdjustError('La cantidad debe ser mayor a cero'); return; }
    setMatAdjustSaving(true);
    try {
      await api.adjustMaterialStock({
        materialId: matAdjust.material.id,
        type: matAdjust.type,
        quantity: matAdjust.quantity,
        reason: finalReason,
        notes: matAdjust.notes || undefined,
        lotNumber: matAdjust.type === 'IN' ? (matAdjust.lotNumber || undefined) : undefined,
        expirationDate: matAdjust.type === 'IN' ? (matAdjust.expirationDate || undefined) : undefined,
        supplierId: matAdjust.type === 'IN' && matAdjust.supplierId !== '' ? matAdjust.supplierId : undefined,
      });
      setMatAdjust(null);
      await load();
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message || 'No se pudo guardar el ajuste');
    } finally {
      setMatAdjustSaving(false);
    }
  };

  const openTraceability = async (p: Product) => {
    try {
      setTraceLoading(true);
      const data = await api.getProductTraceability(p.id);
      setTraceData(data);
      setTraceOpen(true);
    } finally {
      setTraceLoading(false);
    }
  };

  // Totales arriba
  const totalValue = useMemo(() => {
    const useCost = valuationMode === 'cost';
    return (products || []).reduce((acc, p) => {
      const unit = useCost ? (p.cost || 0) : (p.price || 0);
      const stock = (p.totalStock ?? 0);
      return acc + unit * stock;
    }, 0);
  }, [products, valuationMode]);

  const totalsByProduct = useMemo(() => {
    const useCost = valuationMode === 'cost';
    return (products || []).map(p => {
      const unit = useCost ? (p.cost || 0) : (p.price || 0);
      const stock = (p.totalStock ?? 0);
      return { id: p.id, name: p.name, stock, value: unit * stock };
    });
  }, [products, valuationMode]);
  const totalsByDeposit = useMemo(() => {
    const map: Record<string, { stock: number; value: number }> = {};
    for (const p of products) {
      const perLoc = perProductLocStocks[p.id] || [];
      for (const ls of perLoc) {
        if (!map[ls.location]) map[ls.location] = { stock: 0, value: 0 };
        map[ls.location].stock += ls.stock || 0;
        const unit = valuationMode === 'cost' ? (p.cost || 0) : (p.price || 0);
        map[ls.location].value += (ls.stock || 0) * unit;
      }
    }
    return map;
  }, [products, perProductLocStocks, valuationMode]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Inventario</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
          <Button size="small" variant={stockTab==='products' ? 'contained' : 'outlined'} onClick={() => setStockTab('products')}>Productos</Button>
          <Button size="small" variant={stockTab==='materials' ? 'contained' : 'outlined'} onClick={() => setStockTab('materials')}>Materia Prima</Button>
          <Button size="small" variant={viewMode==='product' ? 'contained' : 'outlined'} onClick={() => setViewMode('product')}>Por producto</Button>
          <Button size="small" variant={viewMode==='warehouse' ? 'contained' : 'outlined'} onClick={() => setViewMode('warehouse')}>Por almacén</Button>
          <Button size="small" startIcon={<AddCircle />} onClick={() => openWarehouseDialog()} color="secondary" variant="outlined">Depósitos</Button>
        </Stack>
      </Stack>

      <TextField
        size="small"
        placeholder="Buscar por nombre, SKU o código de materia prima..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search /></InputAdornment>
          ),
        }}
        sx={{ mb: 2, maxWidth: 400 }}
        fullWidth
      />

      {/* Valor total del inventario - arriba */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Valor total del inventario</Typography>
              <Typography variant="h3" fontWeight={700}>${totalValue.toLocaleString()}</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Valorizado a:</Typography>
              <Button
                size="small"
                variant={valuationMode === 'cost' ? 'contained' : 'outlined'}
                onClick={() => setValuationMode('cost')}
                sx={{
                  bgcolor: valuationMode === 'cost' ? 'white' : 'transparent',
                  color: valuationMode === 'cost' ? 'primary.main' : 'white',
                  borderColor: 'white',
                  '&:hover': { bgcolor: valuationMode === 'cost' ? 'grey.100' : 'rgba(255,255,255,0.1)' }
                }}
              >
                Costo
              </Button>
              <Button
                size="small"
                variant={valuationMode === 'price' ? 'contained' : 'outlined'}
                onClick={() => setValuationMode('price')}
                sx={{
                  bgcolor: valuationMode === 'price' ? 'white' : 'transparent',
                  color: valuationMode === 'price' ? 'primary.main' : 'white',
                  borderColor: 'white',
                  '&:hover': { bgcolor: valuationMode === 'price' ? 'grey.100' : 'rgba(255,255,255,0.1)' }
                }}
              >
                Venta
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {hasFlag('inventory-ml-full-flex') && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Envíos a ML FULL</Typography>
                <Typography variant="body2" color="text.secondary">
                  Registra lo que despachás a ML FULL; la recepción real se lee desde Mercado Libre (stock meli_facility).
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
                <Button size="small" variant="outlined" onClick={() => loadShipments()}>Refrescar</Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<LocalShipping />}
                  onClick={() => {
                    setShipLines([]);
                    setShipRef('');
                    setShipNotes('');
                    setShipOpen(true);
                  }}
                >
                  Nuevo envío
                </Button>
              </Stack>
            </Stack>
            <Stack component="ul" sx={{ pl: 2, mt: 1, mb: 2 }}>
              <Typography component="li" variant="body2">Generá la etiqueta en ML, embalá y entregá/coordina retiro.</Typography>
              <Typography component="li" variant="body2">Registrá el envío aquí para descontar Flex/propio y dejarlo “en tránsito a Full”.</Typography>
              <Typography component="li" variant="body2">Cuando ML lo reciba, el stock aparecerá en la ubicación `meli_facility` (lectura desde la API).</Typography>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Producto / Inventario</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell>Referencia</TableCell>
                    <TableCell>Notas / Tipo</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={s.source || 'Local'} color={s.source === 'ML' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>{s.productName || '—'}</TableCell>
                      <TableCell align="right">{s.quantity}</TableCell>
                      <TableCell>{s.referenceNumber || '—'}</TableCell>
                      <TableCell>{s.notes || '—'}</TableCell>
                      <TableCell>{s.userName || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {shipments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">Aún no registraste envíos a Full.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={shipPage <= 1}
                onClick={() => loadShipments(shipPage - 1)}
              >
                Anterior
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!shipHasMore && shipPage >= shipTotalPages}
                onClick={() => loadShipments(shipPage + 1)}
              >
                Traer más
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {viewMode === 'product' && stockTab === 'materials' && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell align="right">Costo</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="center">Punto pedido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMaterials.map(m => (
                <TableRow key={m.id} hover sx={{ bgcolor: m.currentStock <= (m.reorderPoint || 0) ? '#FFF3E0' : undefined }}>
                  <TableCell>{m.code || '-'}</TableCell>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.unit}</TableCell>
                  <TableCell align="right">${Number(m.cost || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{m.currentStock}</TableCell>
                  <TableCell align="center">{m.reorderPoint}</TableCell>
                </TableRow>
              ))}
              {filteredMaterials.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">{materials.length === 0 ? 'Sin materias primas cargadas' : 'Ninguna materia prima coincide con la búsqueda'}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {viewMode === 'product' && stockTab === 'products' && (
      <TableContainer>
        <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Propio</TableCell>
                <TableCell align="right">Secundario</TableCell>
                <TableCell align="right">ML FULL</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="center">Mín</TableCell>
                <TableCell align="center">Reposición</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {products.map(p => (
              <React.Fragment key={p.id}>
              <TableRow hover>
                <TableCell>{p.sku}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell align="right">{p.currentStock}</TableCell>
                <TableCell align="right">{p.secondaryStock ?? 0}</TableCell>
                <TableCell align="right">{p.mlFullStock ?? 0}</TableCell>
                <TableCell align="right"><Typography fontWeight={700}>{p.totalStock ?? p.currentStock}</Typography></TableCell>
                <TableCell align="center">{p.minStock}</TableCell>
                <TableCell align="center">{p.reorderPoint}</TableCell>
                <TableCell align="right">
                  {canAdjustStock && (
                    <Tooltip title="Ajustar stock">
                      <IconButton size="small" onClick={() => openAdjust(p)}><Edit /></IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Transferencia interna">
                    <IconButton size="small" onClick={async () => { setSelected(p); setTransferOpen(true); setTransferQty(0); setFromLoc('Principal'); setToLoc(locations.find(x => x !== 'Principal') || 'Principal'); const stocks = await api.getLocationStocks(p.id); setLocStocks(stocks.items); }}>
                      <LocalShipping />
                    </IconButton>
                  </Tooltip>
                  {hasFlag('inventory-ml-full-flex') && (
                    <Tooltip title="Enviar a ML FULL">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelected(p);
                          setShipLines([{ productId: p.id, quantity: 0 }]);
                          setShipRef('');
                          setShipNotes('');
                          setShipOpen(true);
                        }}
                      >
                        <LocalShipping />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Ver trazabilidad">
                    <IconButton size="small" onClick={() => openTraceability(p)}><Timeline /></IconButton>
                  </Tooltip>
                  <Tooltip title={expandedProductId === p.id ? 'Ocultar lotes' : 'Ver lotes con stock'}>
                    <IconButton size="small" onClick={() => toggleExpandProduct(p.id)}>
                      {expandedProductId === p.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              {expandedProductId === p.id && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ bgcolor: 'grey.50' }}>
                    {!expandedLots[p.id] && <Typography variant="body2" color="text.secondary">Cargando lotes...</Typography>}
                    {expandedLots[p.id] && expandedLots[p.id].length === 0 && (
                      <Typography variant="body2" color="text.secondary">No hay lotes con stock para este producto.</Typography>
                    )}
                    {expandedLots[p.id] && expandedLots[p.id].length > 0 && (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Lote</TableCell>
                            <TableCell align="right">Cantidad disponible</TableCell>
                            <TableCell>Vencimiento</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {expandedLots[p.id].map((b: any) => (
                            <TableRow key={b.id}>
                              <TableCell>{b.batchCode || b.publicId}</TableCell>
                              <TableCell align="right">{b.quantity}</TableCell>
                              <TableCell>{b.expirationDate ? String(b.expirationDate).slice(0, 10) : 'Sin vencimiento'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {viewMode === 'warehouse' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
          {locations.map((loc) => {
            const locData = totalsByDeposit[loc];
            return (
              <Card key={loc} sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{loc}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {locationInventory[loc]?.length ? `${locationInventory[loc].length} productos` : 'Sin stock'}
                        {locData && ` · $${locData.value.toLocaleString()}`}
                      </Typography>
                    </Box>
                    <Chip size="small" label={getLocationTag(loc).label} color={getLocationTag(loc).color} />
                  </Stack>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Producto</TableCell>
                          <TableCell align="right">Stock</TableCell>
                          <TableCell align="right">Valor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(locationInventory[loc] || []).map(({ product, stock }) => {
                          const unit = valuationMode === 'cost' ? (product.cost || 0) : (product.price || 0);
                          return (
                            <TableRow key={product.id} hover>
                              <TableCell>{product.name}</TableCell>
                              <TableCell align="right">{stock}</TableCell>
                              <TableCell align="right">${(stock * unit).toLocaleString()}</TableCell>
                            </TableRow>
                          );
                        })}
                        {(!locationInventory[loc] || locationInventory[loc].length === 0) && (
                          <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">Sin productos en este depósito</Typography></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}


      {/* Inventario de materias primas e insumos (solo en vista de productos) */}
      {stockTab === 'products' && <Box sx={{ mt: 4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          Materias primas e insumos
        </Typography>
        <Card>
          <CardContent>
            {materials.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No hay materias primas ni insumos cargados.
              </Typography>
            )}
            {materials.length > 0 && filteredMaterials.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Ninguna materia prima ni insumo coincide con la búsqueda.
              </Typography>
            )}
            {filteredMaterials.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Unidad</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      <TableCell align="right">Punto de reposición</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMaterials.map(m => (
                      <TableRow key={m.id} hover>
                        <TableCell>{m.code || '—'}</TableCell>
                        <TableCell>{m.name}</TableCell>
                        <TableCell>{m.unit || m.purchaseUnitName || '—'}</TableCell>
                        <TableCell align="right">{m.currentStock ?? 0}</TableCell>
                        <TableCell align="right">{m.reorderPoint ?? 0}</TableCell>
                        <TableCell align="right">
                          {canAdjustStock && (
                            <Tooltip title="Ajuste manual de stock">
                              <IconButton size="small" onClick={() => openMatAdjust(m)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajuste de stock</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adjustError && <Typography variant="body2" color="error">{adjustError}</Typography>}
            <TextField label="Producto" value={selected?.name || ''} fullWidth disabled />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="number" label="Stock actual" value={selected?.currentStock ?? 0} fullWidth disabled />
              <TextField type="number" label="Nuevo stock" value={newStock} onChange={e => setNewStock(Number(e.target.value))} inputProps={{ step: 0.001 }} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Motivo" value={reason} onChange={e => setReason(e.target.value)} fullWidth>
                {ADJUST_REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              {reason === 'Otro' && (
                <TextField label="Detalle del motivo" value={customReason} onChange={e => setCustomReason(e.target.value)} fullWidth required />
              )}
            </Stack>
            {newStock > (selected?.currentStock ?? 0) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Lote del ingreso (opcional)" value={adjustLot} onChange={e => setAdjustLot(e.target.value)} fullWidth />
                <TextField label="Vencimiento" type="date" InputLabelProps={{ shrink: true }} value={adjustExpiration} onChange={e => setAdjustExpiration(e.target.value)} fullWidth />
              </Stack>
            )}
            {newStock < (selected?.currentStock ?? 0) && (
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={adjustUseLots} onChange={e => setAdjustUseLots(e.target.checked)} />}
                  label="Elegir de qué lote sale la baja (si no, sale del más viejo)"
                />
                {adjustUseLots && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {adjustLotsList.length === 0 && (
                      <Typography variant="body2" color="text.secondary">Este producto no tiene lotes con stock.</Typography>
                    )}
                    {adjustLotsList.length > 0 && adjustLotsList.every((b: any) => !b.isReleased) && (
                      <Typography variant="body2" color="warning.main">
                        Los lotes con stock están pendientes de liberación de calidad. Se pueden dar de baja cuando se liberen.
                      </Typography>
                    )}
                    {adjustLotsList.map((b: any) => (
                      <Stack key={b.id} direction="row" spacing={2} alignItems="center">
                        <Typography sx={{ flex: 1 }} color={b.isReleased ? 'text.primary' : 'text.disabled'}>
                          Lote {b.batchCode || b.publicId} · disponible {b.quantity}
                          {!b.isReleased && ' · pendiente de liberación'}
                        </Typography>
                        <TextField
                          label="Sacar"
                          type="number"
                          size="small"
                          sx={{ width: 140 }}
                          disabled={!b.isReleased}
                          value={adjustLotQty[b.id] ?? ''}
                          onChange={e => setAdjustLotQty(prev => ({ ...prev, [b.id]: e.target.value }))}
                        />
                      </Stack>
                    ))}
                    <Typography variant="caption" color="text.secondary">
                      Total por lotes: {adjustLotsList.filter((b: any) => b.isReleased).reduce((acc, b: any) => acc + Number(adjustLotQty[b.id] || 0), 0)} de {(selected?.currentStock ?? 0) - newStock} a dar de baja
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            <TextField label="Notas" value={notes} onChange={e => setNotes(e.target.value)} fullWidth multiline minRows={2} />
            <Typography variant="caption" color="text.secondary">
              El ajuste queda registrado con tu usuario, el motivo y el stock antes/después. Si sumás stock con lote y vencimiento, se crea el lote correspondiente.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveAdjust}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!matAdjust} onClose={() => setMatAdjust(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajuste de stock — materia prima</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adjustError && <Typography variant="body2" color="error">{adjustError}</Typography>}
            <TextField label="Materia prima" value={matAdjust?.material.name || ''} fullWidth disabled />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Tipo" value={matAdjust?.type || 'IN'} onChange={e => setMatAdjust(d => (d ? { ...d, type: e.target.value as 'IN' | 'OUT' } : d))} sx={{ minWidth: 160 }}>
                <MenuItem value="IN">Ingreso (+)</MenuItem>
                <MenuItem value="OUT">Baja (−)</MenuItem>
              </TextField>
              <TextField
                type="number"
                label={`Cantidad (stock actual: ${matAdjust?.material.currentStock ?? 0})`}
                value={matAdjust?.quantity ?? 0}
                onChange={e => setMatAdjust(d => (d ? { ...d, quantity: Math.max(0, Number(e.target.value || 0)) } : d))}
                inputProps={{ min: 0, step: 0.001 }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Motivo" value={matAdjust?.reason || ''} onChange={e => setMatAdjust(d => (d ? { ...d, reason: e.target.value } : d))} fullWidth>
                {ADJUST_REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              {matAdjust?.reason === 'Otro' && (
                <TextField label="Detalle del motivo" value={matAdjust?.customReason || ''} onChange={e => setMatAdjust(d => (d ? { ...d, customReason: e.target.value } : d))} fullWidth required />
              )}
            </Stack>
            {matAdjust?.type === 'IN' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Lote del ingreso" value={matAdjust?.lotNumber || ''} onChange={e => setMatAdjust(d => (d ? { ...d, lotNumber: e.target.value } : d))} fullWidth />
                <TextField label="Vencimiento" type="date" InputLabelProps={{ shrink: true }} value={matAdjust?.expirationDate || ''} onChange={e => setMatAdjust(d => (d ? { ...d, expirationDate: e.target.value } : d))} fullWidth />
              </Stack>
            )}
            {matAdjust?.type === 'IN' && (
              <TextField
                select
                label="Proveedor (opcional)"
                value={matAdjust?.supplierId ?? ''}
                onChange={e => setMatAdjust(d => (d ? { ...d, supplierId: e.target.value === '' ? '' : Number(e.target.value) } : d))}
                fullWidth
              >
                <MenuItem value="">Sin proveedor</MenuItem>
                {adjustSuppliers.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField label="Notas" value={matAdjust?.notes || ''} onChange={e => setMatAdjust(d => (d ? { ...d, notes: e.target.value } : d))} fullWidth multiline minRows={2} />
            <Typography variant="caption" color="text.secondary">
              Los ingresos crean un lote con su vencimiento; las bajas descuentan de los lotes por FEFO (vence antes, sale antes). Todo queda registrado con tu usuario y el motivo.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatAdjust(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveMatAdjust} disabled={matAdjustSaving}>{matAdjustSaving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!prodBatchEdit} onClose={() => setProdBatchEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar lote de producto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adjustError && <Typography variant="body2" color="error">{adjustError}</Typography>}
            <TextField
              label="Código de lote"
              value={prodBatchEdit?.batchCode || ''}
              inputProps={{ maxLength: 100 }}
              onChange={e => setProdBatchEdit(d => (d ? { ...d, batchCode: e.target.value } : d))}
              fullWidth
            />
            <TextField
              label="Vencimiento"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={prodBatchEdit?.expirationDate || ''}
              onChange={e => setProdBatchEdit(d => (d ? { ...d, expirationDate: e.target.value } : d))}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              La corrección queda registrada en Movimientos con tu usuario y los valores anteriores.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProdBatchEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveProdBatchEdit} disabled={prodBatchSaving}>{prodBatchSaving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!retentionDialog} onClose={() => setRetentionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cargar muestra de retención</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adjustError && <Typography variant="body2" color="error">{adjustError}</Typography>}
            <Typography variant="caption" color="text.secondary">
              Se descuenta del stock disponible de este lote (no queda como stock vendible). Es requisito para poder liberarlo a la venta.
            </Typography>
            <TextField
              label="Cantidad a retención"
              type="number"
              value={retentionDialog?.quantityRetention || ''}
              onChange={e => setRetentionDialog(d => (d ? { ...d, quantityRetention: e.target.value } : d))}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Cantidad a análisis"
              type="number"
              value={retentionDialog?.quantityAnalysis || ''}
              onChange={e => setRetentionDialog(d => (d ? { ...d, quantityAnalysis: e.target.value } : d))}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Notas (opcional)"
              value={retentionDialog?.notes || ''}
              onChange={e => setRetentionDialog(d => (d ? { ...d, notes: e.target.value } : d))}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetentionDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveRetentionSample} disabled={retentionSaving}>{retentionSaving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transferencia interna</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Producto" value={selected?.name || ''} fullWidth disabled />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Desde</InputLabel>
                <Select value={fromLoc} label="Desde" onChange={e => setFromLoc(e.target.value)}>
                  {locations.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Hacia</InputLabel>
                <Select value={toLoc} label="Hacia" onChange={e => setToLoc(e.target.value)}>
                  {locations.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <TextField type="number" label="Cantidad" value={transferQty} onChange={e => setTransferQty(Number(e.target.value))} fullWidth />
            <Typography variant="body2" color="text.secondary">Stock por ubicación:</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Ubicación</TableCell><TableCell align="right">Stock</TableCell></TableRow></TableHead>
                <TableBody>
                  {locStocks.map(s => <TableRow key={s.location}><TableCell>{s.location}</TableCell><TableCell align="right">{s.stock}</TableCell></TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={async () => { if (!selected || transferQty <= 0) return; await api.transferInternal({ productId: selected.id, fromLocation: fromLoc, toLocation: toLoc, quantity: transferQty }); setTransferOpen(false); await load(); if (selected) { const stocks = await api.getLocationStocks(selected.id); setLocStocks(stocks.items); } }}>Transferir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={whDialogOpen} onClose={() => setWhDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Depósitos / Almacenes</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Nombre" value={whForm.name} onChange={e => setWhForm({ ...whForm, name: e.target.value })} fullWidth />
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo" value={whForm.type} onChange={e => setWhForm({ ...whForm, type: e.target.value })}>
                  <MenuItem value="INTERNAL">Interno / Propio</MenuItem>
                  <MenuItem value="ML_FULFILLMENT">ML Fulfillment (Full)</MenuItem>
                  <MenuItem value="THIRD_PARTY">Terceros</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Código" value={whForm.code || ''} onChange={e => setWhForm({ ...whForm, code: e.target.value })} fullWidth />
              <TextField label="Dirección" value={whForm.address || ''} onChange={e => setWhForm({ ...whForm, address: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Notas" value={whForm.notes || ''} onChange={e => setWhForm({ ...whForm, notes: e.target.value })} fullWidth multiline minRows={2} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouses.map(w => (
                    <TableRow key={w.id}>
                      <TableCell>{w.name}</TableCell>
                      <TableCell><Chip size="small" label={w.type === 'ML_FULFILLMENT' ? 'ML Fulfillment' : w.type} color={w.type === 'ML_FULFILLMENT' ? 'warning' : 'default'} /></TableCell>
                      <TableCell>{w.code || '—'}</TableCell>
                      <TableCell>{w.isActive ? 'Activo' : 'Inactivo'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openWarehouseDialog(w)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteWarehouse(w.id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {warehouses.length === 0 && (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Sin depósitos cargados.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhDialogOpen(false)}>Cerrar</Button>
          <Button variant="contained" onClick={saveWarehouse}>{whForm.id ? 'Actualizar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      {hasFlag('inventory-ml-full-flex') && (
        <Dialog open={shipOpen} onClose={() => setShipOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Enviar a ML FULL</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">Seleccioná uno o varios productos y cantidades para registrar el envío a ML FULL.</Typography>
              <Stack spacing={2}>
                {(shipLines.length === 0 ? [{ productId: selected?.id || products[0]?.id, quantity: 0 }] : shipLines).map((line, idx) => {
                  const prod = products.find(pr => pr.id === line.productId);
                  return (
                    <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                      <FormControl fullWidth>
                        <InputLabel>Producto</InputLabel>
                        <Select
                          label="Producto"
                          value={line.productId || ''}
                          onChange={e => {
                            const val = Number(e.target.value);
                            setShipLines(prev => {
                              const copy = [...(prev.length ? prev : [{ productId: val, quantity: 0 }])];
                              copy[idx] = { ...copy[idx], productId: val };
                              return copy;
                            });
                          }}
                        >
                          {products.map(pr => (
                            <MenuItem key={pr.id} value={pr.id}>{pr.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        type="number"
                        label="Cantidad"
                        value={line.quantity}
                        onChange={e => {
                          const val = Number(e.target.value);
                          setShipLines(prev => {
                            const copy = [...(prev.length ? prev : [{ productId: line.productId, quantity: val }])];
                            copy[idx] = { ...copy[idx], quantity: val };
                            return copy;
                          });
                        }}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
                        Disp: {prod?.currentStock ?? 0}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setShipLines(prev => {
                            const copy = [...prev];
                            copy.splice(idx, 1);
                            return copy;
                          });
                        }}
                        disabled={(shipLines.length || 1) === 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}
                <Button
                  size="small"
                  startIcon={<AddCircle />}
                  onClick={() => setShipLines(prev => [...(prev.length ? prev : []), { productId: products[0]?.id || 0, quantity: 0 }])}
                  disabled={products.length === 0}
                >
                  Agregar producto
                </Button>
              </Stack>
              <TextField label="Referencia" value={shipRef} onChange={e => setShipRef(e.target.value)} fullWidth />
              <TextField label="Notas" value={shipNotes} onChange={e => setShipNotes(e.target.value)} fullWidth multiline minRows={2} />
              <Typography variant="caption" color="text.secondary">Esto descuenta del stock propio y registra el envío en tránsito a ML FULL. La recepción se concilia desde MercadoLibre.</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShipOpen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={async () => {
                const lines = (shipLines.length ? shipLines : [{ productId: selected?.id || 0, quantity: 0 }])
                  .filter(l => l.productId && l.quantity > 0);
                if (!lines.length) return;
                for (const l of lines) {
                  await api.createShipmentToMlFull({ productId: l.productId, quantity: l.quantity, referenceNumber: shipRef, notes: shipNotes });
                }
                setShipOpen(false);
                await load();
                await loadShipments();
              }}
            >
              Crear envío
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={traceOpen} onClose={() => { setTraceOpen(false); setTraceData(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Trazabilidad de producto</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Tip: usá el identificador de lote público que aparece acá para consultar la trazabilidad completa (incluyendo materias primas y lotes comprados) en la página pública de trazabilidad.
          </Typography>
          {traceLoading && (
            <Typography variant="body2">Cargando trazabilidad...</Typography>
          )}
          {!traceLoading && traceData && (
            <Box>
              <Typography variant="h6" gutterBottom>{traceData.productName}</Typography>
              {(traceData.batches || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No hay lotes registrados para este producto.</Typography>
              )}
              {(traceData.batches || []).map((b: any) => (
                <Card key={b.publicId} sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="subtitle2">
                        Lote:{' '}
                        <a
                          href={`/trace/${encodeURIComponent(b.publicId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {b.batchCode || b.publicId}
                        </a>
                      </Typography>
                      {canAdjustStock && (
                        <Tooltip title="Editar lote / vencimiento">
                          <IconButton size="small" onClick={() => setProdBatchEdit({ id: b.id, batchCode: b.batchCode || '', expirationDate: b.expirationDate ? String(b.expirationDate).slice(0, 10) : '' })}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {b.productionOrderId && (
                        <Chip
                          size="small"
                          color={b.isReleased ? 'success' : 'warning'}
                          label={b.isReleased ? 'Liberado a venta' : 'Pendiente de liberación'}
                        />
                      )}
                      {b.productionOrderId && !b.isReleased && !b.hasRetentionSample && (
                        <Chip size="small" color="error" variant="outlined" label="Falta muestra de retención" />
                      )}
                      {b.productionOrderId && !b.isReleased && !b.hasRetentionSample && canReleaseBatch && (
                        <Button size="small" variant="outlined" onClick={() => setRetentionDialog({ batchId: b.id, quantityRetention: '', quantityAnalysis: '', notes: '' })}>
                          Cargar muestra
                        </Button>
                      )}
                      {b.productionOrderId && !b.isReleased && b.hasRetentionSample && canReleaseBatch && (
                        <Button size="small" variant="outlined" disabled={releasingBatchId === b.id} onClick={() => releaseProdBatch(b.id)}>
                          {releasingBatchId === b.id ? 'Liberando…' : 'Liberar'}
                        </Button>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Stock actual: {b.quantity} · Costo unitario: ${b.unitCost?.toLocaleString?.() ?? b.unitCost} · Ubicación: {b.location}
                    </Typography>
                    {b.productionOrderId && (
                      <Typography variant="body2" color="text.secondary">
                        OP origen: {b.productionOrderNumber || `#${b.productionOrderId}`} {b.producedAt ? `· Producido: ${new Date(b.producedAt).toLocaleString()}` : ''}
                      </Typography>
                    )}
                    {b.productionOrderId && b.isReleased && b.releasedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Liberado: {new Date(b.releasedAt).toLocaleString()}{b.releasedByName ? ` · Por: ${b.releasedByName}` : ''}
                      </Typography>
                    )}
                    {(b.components || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Componentes usados:</Typography>
                        {(b.components || []).map((c: any, idx: number) => (
                          <Box key={idx} sx={{ ml: 1, mt: 0.5 }}>
                            <Typography variant="body2">
                              - [{c.type}] {c.name} · Req: {c.quantityRequired}
                            </Typography>
                            {(c.batches || []).length > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                Lotes: {(c.batches || []).map((cb: any) => {
                                  const baseLabel = `${cb.lotCode || cb.batchPublicId} (qty ${cb.quantityUsed})`;
                                  const extra: string[] = [];
                                  if (cb.supplierName) extra.push(`Prov: ${cb.supplierName}`);
                                  if (cb.purchaseReference) extra.push(`Ref: ${cb.purchaseReference}`);
                                  if (cb.expirationDate) extra.push(`Vence: ${new Date(cb.expirationDate).toLocaleDateString()}`);
                                  return extra.length ? `${baseLabel} [${extra.join(' · ')}]` : baseLabel;
                                }).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTraceOpen(false); setTraceData(null); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
